import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "@/services/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { Button } from "@/components/Button";
import { TextField } from "@/components/Field";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PASSWORD_PROBLEM_KEY, passwordProblems } from "@/lib/passwordPolicy";
import { isLocale } from "@/i18n";

/**
 * Where a sign-in link lands.
 *
 * The welcome mail carries a one-time code minted by Firebase. Firebase would
 * host the page that redeems it, on the project's own domain — a domain the
 * person was never told about, so they distrust the link (the founder did,
 * 2026-09-09). This page is that handler on our address: it checks the code,
 * shows whose account it is for, takes a password once and repeated, and
 * signs the person straight in. Nothing about the password leaves the browser
 * except to the identity platform.
 *
 * The forgotten-password mails Firebase sends itself land here too, once the
 * project's action URL points at this route.
 */
type State =
  | { kind: "checking" }
  | { kind: "ready"; email: string }
  | { kind: "invalid" }
  | { kind: "done" };

export function AuthActionPage() {
  const t = useT();
  const { setLocale } = useI18n();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { showError } = useToast();
  const [params] = useSearchParams();

  const mode = params.get("mode");
  const code = params.get("oobCode") ?? "";
  const lang = params.get("lang");

  const [state, setState] = useState<State>({ kind: "checking" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // The mail was written in the person's language; the page opens in it too.
  useEffect(() => {
    if (isLocale(lang)) setLocale(lang);
  }, [lang, setLocale]);

  useEffect(() => {
    if (mode !== "resetPassword" || !code) return;
    let cancelled = false;
    verifyPasswordResetCode(auth(), code)
      .then((email) => {
        if (!cancelled) setState({ kind: "ready", email });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "invalid" });
      });
    return () => {
      cancelled = true;
    };
  }, [mode, code]);

  // Only password links are minted for this product. Anything else is not
  // ours to handle, and the sign-in page is the honest place to send it.
  if (mode !== "resetPassword" || !code) return <Navigate to="/login" replace />;

  const problems = password.length > 0 ? passwordProblems(password) : [];
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    state.kind === "ready" &&
    problems.length === 0 &&
    password.length > 0 &&
    confirm === password &&
    !busy;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit || state.kind !== "ready") return;

    setBusy(true);
    try {
      await confirmPasswordReset(auth(), code, password);
    } catch (error) {
      // The code was fine a moment ago and is not now: used from another tab,
      // or the hour ran out while the form was open.
      setBusy(false);
      setState({ kind: "invalid" });
      showError(error);
      return;
    }
    try {
      await signIn(state.email, password);
      navigate("/", { replace: true });
    } catch {
      // The password is set either way; the sign-in page takes it from here.
      setState({ kind: "done" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <div className="flex items-center justify-between px-6 py-4">
        {/* The wordmark, above the card and in the serif, as on the sign-in page. */}
        <p className="font-display text-xl text-ink">{t("app.name")}</p>
        <LanguageSwitcher />
      </div>

      <main className="flex flex-1 items-start justify-center px-4 pb-24 pt-6">
        <div className="card w-full max-w-sm p-8">
          <h1 className="page-title">{t("auth.action.title")}</h1>

          {state.kind === "checking" ? (
            <p className="mt-2 text-[14px] text-ink-muted" role="status">
              {t("auth.action.checking")}
            </p>
          ) : null}

          {state.kind === "invalid" ? (
            <>
              <p className="mt-2 text-[14px] text-ink-muted" role="alert">
                {t("auth.action.invalid")}
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block text-[14px] font-medium text-brand underline underline-offset-2 hover:text-brand-hover"
              >
                {t("auth.action.toSignIn")}
              </Link>
            </>
          ) : null}

          {state.kind === "done" ? (
            <>
              <p className="mt-2 text-[14px] text-ink-muted" role="status">
                {t("auth.action.done")}
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block text-[14px] font-medium text-brand underline underline-offset-2 hover:text-brand-hover"
              >
                {t("auth.action.toSignIn")}
              </Link>
            </>
          ) : null}

          {state.kind === "ready" ? (
            <>
              <p className="mt-2 text-[14px] text-ink-muted">
                {t("auth.action.for", { email: state.email })}
              </p>
              <form className="mt-8 flex flex-col gap-4" onSubmit={onSubmit}>
                <TextField
                  labelKey="auth.newPassword"
                  hintKey="password.rules"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  required
                  value={password}
                  errorKey={problems[0] ? PASSWORD_PROBLEM_KEY[problems[0]] : undefined}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <TextField
                  labelKey="auth.confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  errorKey={mismatch ? "auth.passwordMismatch" : undefined}
                  onChange={(event) => setConfirm(event.target.value)}
                />
                <Button type="submit" busy={busy} disabled={!canSubmit} className="mt-2 w-full">
                  {t("auth.action.save")}
                </Button>
              </form>
              <p className="mt-6 text-[12px] text-ink-faint">{t("auth.sameAccount")}</p>
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
}
